import { useEffect, useRef } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { coursesApi } from '@/features/editor/coursesApi'
import { useCourseJob } from '@/features/ai/useCourseJob'

interface Seen {
  jobId: number | null
  done: number
}

/**
 * Курс наполняется на глазах: как только счётчик записанных уроков вырос —
 * перечитываем карточку курса, и в таймлайне появляется новый урок.
 *
 * Блоки уроков НЕ тянем: они грузятся при открытии урока, как и раньше. Один
 * запрос на урок вместо двух, а главное расхождение («прогресс 5 из 10 при
 * пустом редакторе читается как поломка») снято.
 *
 * Только `course_generation` (addendum п.2): у остальных трёх типов прогона
 * `progress.done` тоже растёт (картинки/блоки/перевод), но новых уроков от
 * них не появляется — перечитывать карточку курса незачем.
 *
 * Память о просмотренном — пара `{ jobId, done }`, а не голый счётчик
 * (addendum п.1): без привязки к джобе второй прогон подряд на тот же курс
 * (без ухода со страницы), у которого `done` меньше хвоста первого, не
 * инвалидировал бы карточку вообще. Смена джобы сбрасывает базовую линию —
 * рост считается заново от нуля.
 */
export const useLiveLessons = (courseId: number): void => {
  const dispatch = useAppDispatch()
  const { entry, progress } = useCourseJob(courseId)
  const seen = useRef<Seen>({ jobId: null, done: 0 })

  useEffect(() => {
    if (entry?.type !== 'course_generation') return
    const jobId = entry.jobId
    const baseline = seen.current.jobId === jobId ? seen.current.done : 0
    if (progress.done <= baseline) return
    seen.current = { jobId, done: progress.done }
    dispatch(coursesApi.util.invalidateTags([{ type: 'Course', id: courseId }]))
  }, [entry?.jobId, entry?.type, progress.done, courseId, dispatch])
}
